import Layout from 'components/ui/Layout';
import { AddAdminModal } from 'features/admin/components/AddAdminModal';
import {
  AdminStatus,
  TwoFactorAuthStatus,
} from 'features/admin/types/admin.type';
import { usePrefetchAdmins } from 'features/admin/hooks/usePrefetchAdmins';
import { useURLSync } from 'features/admin/hooks/useURLSync';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Eye,
  Search,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAdmins } from 'features/admin/hooks/useAdmins';
import { formatDateTime } from 'utils/dateFormatter';
import { EditAdminModal } from 'features/admin/components/EditAdminModal';
import { DeleteConfirmModal } from 'features/admin/components/DeleteConfirmModal';
import { ViewAdminModal } from 'features/admin/components/ViewModal';
  import { useUserProfile } from "features/auth/hooks/useUserProfile";

  import { adminServices } from 'features/admin/services/admin.service';


const AdminList = () => {
  const [filters, setFilters] = useURLSync({
    page: '1',
    limit: '10',
    status: 'all',
    role: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    twoFactorAuth: 'all',
  });

  // const [searchInput, setSearchInput] = useState(filters.search);
  const [searchInput, setSearchInput] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch admins
  const { data, isPending, isError, error } = useAdmins(filters);

  const { data: profileData } = useUserProfile();
  const user = profileData?.data;

  // console.log('Current Role:', user?.role?.name);

  const restrictedRoles = [
    "delivery_manager",
    "category_manager",
    "operations_manager",
    "travel_manager",
    "finance_manager"
  ];

  const showPermissionError = restrictedRoles.includes(user?.role?.name);

  // Prefetch
  const { prefetchNextPage, prefetchPrevPage } = usePrefetchAdmins();

  // Prefetch adjacent pages
  useEffect(() => {
    if (data?.pagination) {
      const { currentPage, totalPages } = data.pagination;
      prefetchNextPage(filters, totalPages);
      if (currentPage > 1) {
        prefetchPrevPage(filters);
      }
    }
  }, [data, filters]);

  // Update filter and reset to page 1
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : '1',
    }));
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  // Sort handler
  const handleSort = (column) => {
    const newOrder =
      filters.sortBy === column && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: newOrder,
    }));
  };

  // Export handler
const handleExport = async () => {
  try {
    const response = await adminServices.exportAdmins({
      status: filters.status,
      role: filters.role,
      search: filters.search,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      twoFactorAuth: filters.twoFactorAuth,
    });

    const blob = new Blob([response.data], {
      type: 'text/csv',
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `admins-${Date.now()}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to export admins:', error);
  }
};

  // Toggle status handler
  const handleToggleStatus = (adminId) => {
    // TODO: Implement toggle status functionality
    // console.log('Toggle status for admin:', adminId);
  };

  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column)
      return <span className='text-muted-foreground'>↕</span>;
    return filters.sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  const handleEditClick = (user) => {
    setIsEditModalOpen(true);
    setSelectedUser(user);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleViewClick = (user) => {
    setSelectedUser(user);
    setIsViewModalOpen(true);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background text-foreground p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-1">
                Admin Management
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage administrators, roles and permissions
              </p>
            </div>

            <div className="flex space-x-3">
              {!showPermissionError && (
                <button
                  onClick={() => setIsAddModalOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  <UserPlus size={16} />
                  <span>Add Admin</span>
                </button>
              )}

             {!showPermissionError && (
  <button
    onClick={handleExport}
    className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
  >
    <Download size={16} />
    <span>Export</span>
  </button>
)}
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={16}
              />
              <input
                type="text"
                placeholder="Search by name, email or phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-10 py-2 text-sm"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
              className="px-3 py-2 text-sm"
            >
              <option value="all">All Status</option>
              <option value={AdminStatus.ACTIVE}>Active</option>
              <option value={AdminStatus.INACTIVE}>Inactive</option>
              <option value={AdminStatus.SUSPENDED}>Suspended</option>
            </select>

            {/* 2FA Filter */}
            {/* <select
                value={filters.twoFactorAuth}
                onChange={(e) => updateFilter('twoFactorAuth', e.target.value)}
                className='px-3 py-2 text-sm'
              >
                <option value='all'>All 2FA Status</option>
                <option value={TwoFactorAuthStatus.ENABLED}>2FA Enabled</option>
                <option value={TwoFactorAuthStatus.DISABLED}>2FA Disabled</option>
              </select> */}

            {/* Items per page */}
            <select
              value={filters.limit}
              onChange={(e) => updateFilter("limit", e.target.value)}
              className="px-3 py-2 text-sm"
            >
              <option value="5">5 per page</option>
              <option value="10">10 per page</option>
              <option value="20">20 per page</option>
              <option value="50">50 per page</option>
            </select>
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Name</span>
                        <SortIcon column="name" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("email")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Email</span>
                        <SortIcon column="email" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Role
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        <SortIcon column="status" />
                      </div>
                    </th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      2FA
                    </th> */}
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground"
                      onClick={() => handleSort("createdAt")}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created</span>
                        <SortIcon column="createdAt" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {showPermissionError ? (
                    <tr>
                      <td
                        colSpan="11"
                        className="px-4 py-8 text-center text-destructive"
                      >
                        Error loading CMS: You do not have permission to perform
                        this action!
                      </td>
                    </tr>
                  ) : isPending ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="px-4 py-8 text-center text-muted-foreground"
                      >
                        Loading admins...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="px-4 py-8 text-center text-destructive"
                      >
                        Error loading admins:{" "}
                        {error?.message || "Something went wrong"}
                      </td>
                    </tr>
                  ) : data?.data?.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="px-4 py-8 text-center text-muted-foreground"
                      >
                        No admins found
                      </td>
                    </tr>
                  ) : (
                    data?.data?.map((admin) => (
                      <tr
                        key={admin._id}
                        className="hover:bg-muted/50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-foreground whitespace-nowrap">
                          {admin?.name || "--"}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {admin?.email || "--"}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground">
                          {admin?.phoneNumber || "--"}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {admin.role?.name ? (
                            <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                              {admin.role.name}
                            </span>
                          ) : (
                            "--"
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => handleToggleStatus(admin._id)}
                            className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                              admin.status === AdminStatus.ACTIVE
                                ? "bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500/20"
                                : admin.status === AdminStatus.INACTIVE
                                  ? "bg-muted text-muted-foreground hover:bg-muted/80"
                                  : "bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20"
                            }`}
                          >
                            {admin.status}
                          </button>
                        </td>
                        {/* <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              admin.twoFactorAuth?.isEnabled
                                ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {admin.twoFactorAuth?.isEnabled
                              ? 'Enabled'
                              : 'Disabled'}
                          </span>
                        </td> */}
                        <td className="px-4 py-3 text-sm text-foreground">
                          {formatDateTime(admin.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              className="p-1 hover:bg-muted rounded transition-colors"
                              title="View Details"
                              onClick={() => handleViewClick(admin)}
                            >
                              <Eye
                                size={16}
                                className="text-muted-foreground hover:text-foreground"
                              />
                            </button>
                            <button
                              className="p-1 hover:bg-muted rounded transition-colors"
                              title="Edit Admin"
                            >
                              <Edit
                                size={16}
                                className="text-muted-foreground hover:text-foreground"
                                onClick={() => handleEditClick(admin)}
                              />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(admin)}
                              className="p-1 hover:bg-muted rounded transition-colors"
                              title="Delete Admin"
                            >
                              <Trash2
                                size={16}
                                className="text-muted-foreground hover:text-destructive"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!showPermissionError && data?.pagination && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border space-y-3 sm:space-y-0">
                <div className="text-sm text-muted-foreground">
                  Showing{" "}
                  {(data.pagination.currentPage - 1) *
                    data.pagination.itemsPerPage +
                    1}{" "}
                  to{" "}
                  {Math.min(
                    data.pagination.currentPage * data.pagination.itemsPerPage,
                    data.pagination.totalItems,
                  )}{" "}
                  of {data.pagination.totalItems} admins
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() =>
                      updateFilter(
                        "page",
                        String(data.pagination.currentPage - 1),
                      )
                    }
                    disabled={data.pagination.currentPage === 1}
                    className="p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="flex items-center space-x-1">
                    {[...Array(data.pagination.totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      const current = data.pagination.currentPage;

                      // Show first, last, current, and adjacent pages
                      if (
                        page === 1 ||
                        page === data.pagination.totalPages ||
                        (page >= current - 1 && page <= current + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => updateFilter("page", String(page))}
                            className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                              page === current
                                ? "bg-primary text-primary-foreground font-medium"
                                : "bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === current - 2 || page === current + 2) {
                        return (
                          <span key={page} className="text-muted-foreground">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() =>
                      updateFilter(
                        "page",
                        String(data.pagination.currentPage + 1),
                      )
                    }
                    disabled={
                      data.pagination.currentPage === data.pagination.totalPages
                    }
                    className="p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <AddAdminModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
        />
        <EditAdminModal
          isEditModalOpen={isEditModalOpen}
          setIsEditModalOpen={setIsEditModalOpen}
          user={selectedUser}
        />
        <DeleteConfirmModal
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          user={selectedUser}
        />
        <ViewAdminModal
          isViewModalOpen={isViewModalOpen}
          setIsViewModalOpen={setIsViewModalOpen}
          user={selectedUser}
        />
      </div>
    </Layout>
  );
};

export default AdminList;
